<template>
    <div>
        <div
            v-if="loaded && !(hideDragAreaOnUpload && !canSave)"
            class="drop-target"
            @drop.prevent="dropped"
            @dragover.prevent="dragging"
        >
            <span v-if="qrUrl"><img :src="qrUrl" style="max-width: 120px;"><br><br>Scan above, drag</span>
            <span v-else>Drag </span> files here, select from <strong><a href="#" @click.prevent="selectFiles">your computer</a></strong>
            or <strong><a href="#" @click.prevent="showInTray">in-tray</a></strong>
            <input
                ref="documentSelector"
                @change="documentSelected"
                multiple
                type="file"
                style="display: none;"
                :accept="accept"
            >
        </div>
        <div v-if="loaded" class="container-fluid">
            <slot name="top-callout" v-bind="slotProps"></slot>
            <div v-if="!hideDocumentManagement && attachedDocuments.length > 0" class="row">
                <div class="col mb-4">
                    <ui-button
                        icon="photo_size_select_small"
                        type="secondary"
                        color="primary"
                        @click="toggleReorderMode"
                    >{{ reorderMode ? 'Stop Re-ordering' : 'Re-order' }}</ui-button>
                    <slot name="left-hand-buttons" v-bind="slotProps"></slot>
                </div>
                <div class="col text-right mb-4" style="padding: 0;">
                    <slot name="right-hand-buttons" v-bind="slotProps"></slot>
                    <ui-button
                        icon="delete"
                        type="secondary"
                        color="red"
                        @click="removeAll"
                    >Remove All</ui-button>
                </div>
            </div>
            <draggable v-model="attachedDocuments" class="row" style="margin-left: -35px; margin-right: -35px;" handle=".handle" :scroll-sensitivity="100" :force-fallback="true" @change="rearranged">
            <div v-if="!hideDocumentManagement" v-for="(doc, i) in attachedDocuments" :key="doc.id" :class="reorderMode ? 'col-1' : itemClass">
                <div style="background: #f8f8f8; text-align: center; margin-bottom: .25rem; padding: .25rem" class="handle">
                    <div v-if='urlFor(doc)' class="image-preview">
                        <img @load="doc.is_loaded = true" :src="urlFor(doc, 'w300')" alt="Document Preview" :style="{ maxWidth: '100%', display: 'block', width: doc.is_loaded ? 'auto' : '1px', height: doc.is_loaded ? 'auto' : '1px' }">
                        <image-placeholder v-if="!doc.is_loaded"></image-placeholder>
                        <div class="image-options">
                            <div class="container-fluid" style="height: 100%;">
                                <div class="row align-items-center" style="height: 100%;">
                                    <div class="col-12">
                                        <span class="material-icons" style="padding: .5rem; cursor: grab;">open_with</span>
                                        <ui-icon-button v-if="!reorderMode" type="secondary" color="white" @click.prevent="open(doc)" icon="open_in_new"></ui-icon-button>
                                        <ui-icon-button v-if="!reorderMode" type="secondary" color="white" @click.prevent="remove(doc)" icon="delete"></ui-icon-button>
                                        <slot name="thumbnail-buttons" v-bind="{doc, reorderMode, documents}"></slot>
                                        <template v-if="$scopedSlots['thumbnail-info'] && (doc.created_at || (doc.created_by && doc.created_by.name))">
                                            <ui-icon-button
                                                type="secondary"
                                                color="white"
                                                :aria-label="'Image information'"
                                                :aria-expanded="!!visibleThumbnailInfo[doc.id]"
                                                @click.stop.prevent="toggleThumbnailInfo(doc)"
                                                icon="info_outline"
                                            ></ui-icon-button>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div
                            v-if="visibleThumbnailInfo[doc.id]"
                            class="thumbnail-info-bar"
                            role="button"
                            tabindex="0"
                            aria-label="Hide image information"
                            style="pointer-events: auto; cursor: pointer;"
                            @click.stop="toggleThumbnailInfo(doc)"
                            @keydown.enter.stop.prevent="toggleThumbnailInfo(doc)"
                            @keydown.space.stop.prevent="toggleThumbnailInfo(doc)"
                        >
                            <slot name="thumbnail-info" v-bind="{doc}"></slot>
                        </div>
                    </div>
                    <div style="padding: 3.8rem;" v-else>
                        <ui-progress-circular
                            style="margin:auto;"
                            :stroke="doc.progress > 15 ? 3 : 4"
                            :progress="doc.progress"
                            :type="doc.progress > 15 ? 'determinate' : 'indeterminate'"
                        />
                    </div>
                </div>
                <ui-select
                    v-if="descriptions.length > 0 && !reorderMode"
                    :disabled="doc.deleted"
                    :options="descriptions"
                    v-model="doc.description"
                    @change="doc.is_dirty = true;"
                    style="text-align: center; padding: 0 1rem;"
                ></ui-select>
                <slot name="thumbnail-details" v-bind="{doc}"></slot>
            </div>
            </draggable>
        </div>
        <div class="text-center" v-else>
            <ui-progress-circular
                :size="92"
                :auto-stroke="false"
                :stroke="3"
                v-show="!loaded"
                style="margin: 2rem auto;"
            ></ui-progress-circular>
            Saving changes...
        </div>
        <div v-if="loaded && !noSaveButton">
            <button
                class="btn btn-primary"
                @click="save"
                :disabled="!loaded || !canSave"
            >Save Changes</button>
        </div>

        <in-tray-modal
            ref="inTrayModal"
            @selected="inTrayDocumentsSelected"
            :document-type="defaultType"
        />
    </div>
</template>

<script>
    import draggable from 'vuedraggable'
    import { DocumentResource, DocumentSchema, DocumentTypes } from '../../classes/Api/Document'
    import InTrayModal from './InTrayModal'
    import PromiseQueue from "../../classes/PromiseQueue"
    import Compressor from 'compressorjs'
    import {cloneDeep, sortBy} from "lodash";
    import {randomString} from "../../utils";
    import Bus from "../../events";
    import ImagePlaceholder from "../ImagePlaceholder.vue";
    import DealerKit from "../../api";
    // import heic2any from "heic2any";
    import {AcceptedImageMimes} from "../../../ts/DocumentPaneMimes";

    export default {
        props: {
            descriptions: {
                type: Array,
                default() {
                    return [];
                },
            },
            accept: {
                type: String,
                default: '*',
            },
            defaultType: {
                type: String,
                default: DocumentTypes.TYPE_OTHER,
            },
            modelType: {
                type: String,
                required: false
            },
            modelId: {
                type: [String, Number],
                required: false
            },
            existingDocuments: {
                type: Array,
                required: false,
                default() {
                    return []
                }
            },
            noSaveButton: {
                type: Boolean,
                default: false
            },
            hideDragAreaOnUpload: {
                type: Boolean,
                default: false
            },
            itemClass: {
                type: String,
                default: 'col-4'
            },
            shouldAssociate: {
                type: Boolean,
                default: true
            },
            hideDocumentManagement: {
                type: Boolean,
                default: false
            },
            selectBeforeUpload: {
                type: Boolean,
                default: true
            }
        },
        components: {ImagePlaceholder, draggable, InTrayModal },
        data() {
            return {
                loaded: true,
                canSave: true,
                documents: [],
                uploadQueue: [],
                processingQueue: false,
                paneId: null,
                reorderMode: false,
                visibleThumbnailInfo: {},
            }
        },
        mounted() {
            this.init()

            this.paneId = randomString(10)

            window.Echo.private(`account.${window.marque.ui.account.id}`)
                .listen('.dropbox.document.uploaded', (e) => {
                    if (e.documentManagementPaneId === this.paneId) {
                        const hasUploadOrder = e.order !== null && e.order !== undefined && Number.isFinite(Number(e.order))
                        const uploadOrder = hasUploadOrder ? Number(e.order) : null
                        const order = hasUploadOrder
                            ? this.existingDocuments.length + uploadOrder
                            : this.documents.filter(document => !document.is_deleted).length

                        this.documents.push({...e.document, order, is_dirty: true, is_deleted: false, is_loaded: false})
                        if (hasUploadOrder) {
                            this.documents = sortBy(this.documents, ['order'])
                        }
                        this.emitDocumentCount()
                        this.$emit('selected', this.documents)
                        this.$emit('uploaded', e.document)
                    }
                });
        },
        watch: {
            existingDocuments(newVal, oldVal) {
                this.init();
            }
        },
        methods: {
            documentSelected(e) {
                this.addDocuments(e.target.files)
                setTimeout(() => { e.target.value = null; }, 100)
            },
            setDocumentOrders() {
                const activeDocuments = this.documents.filter(document => !document.is_deleted)
                const deletedDocuments = this.documents.filter(document => document.is_deleted)

                for (let i in activeDocuments) {
                    const order = parseInt(i)
                    if (parseInt(activeDocuments[i].order) !== order) {
                        activeDocuments[i].order = order
                        activeDocuments[i].is_dirty = true
                    }
                }

                this.documents = activeDocuments.concat(deletedDocuments)
            },
            toggleReorderMode() {
                this.reorderMode = !this.reorderMode
            },
            toggleThumbnailInfo(document) {
                if (this.visibleThumbnailInfo[document.id]) {
                    this.$delete(this.visibleThumbnailInfo, document.id)
                } else {
                    this.$set(this.visibleThumbnailInfo, document.id, true)
                }
            },
            rearranged(e) {
                this.$emit('rearranged', this.documents)
            },
            urlFor(document, dimensions = 'w300') {
                if (document.preview_url) {
                    return document.preview_url.replace('_dimensions_', dimensions)
                } else if (document.url) {
                    return document.url.replace('_dimensions_', dimensions)
                }
            },
            showInTray() {
                this.$refs.inTrayModal.open()
            },
            inTrayDocumentsSelected(documents) {
                for (let i in documents) {
                    let doc = cloneDeep(documents[i]);
                    doc.is_dirty = true
                    doc.is_deleted = false
                    doc.is_loaded = false
                    doc.type = this.defaultType
                    doc.description = this.defaultDescription
                    this.documents.push(doc)

                    this.emitDocumentCount()
                }

                this.setDocumentOrders()

                this.$emit('selected', this.documents)

                for (let i of documents) {
                    this.$emit('uploaded', i)
                }
            },
            open(document) {
                Bus.emit('showLibraryDocument', { document })
            },
            remove(doc, warning = true) {
                if (!warning || confirm('Are you sure you want to remove \''+ doc.filename +'\'?')) {
                    doc.is_deleted = true;

                    this.emitDocumentCount()

                    this.$emit('removed', this.documents)
                }
            },
            removeAll() {
                if (confirm('Are you sure you want to remove all images?')) {
                    this.documents.forEach(document => {
                        this.remove(document, false)
                    })
                }
            },
            emitDocumentCount() {
                this.$emit('document-count', this.documents.filter(i => !i.is_deleted).length)
            },
            init() {
                this.documents = [];
                this.loaded = true;

                this.$nextTick(() => {
                    for (let i in this.existingDocuments) {
                        this.documents.push(Object.assign({}, this.existingDocuments[i], {
                            is_dirty: this.existingDocuments[i].is_dirty ?? false,
                            is_deleted: false,
                            is_loaded: false,
                        }));
                    }

                    this.documents = sortBy(this.documents, ['order']);

                    this.setDocumentOrders()

                    this.$emit('document-count', this.documents.length)

                    this.$emit('afterInit')
                })
            },
            selectFiles() {
                this.$refs['documentSelector'].click();
            },
            uploadDocument(document, source, successCallback, failureCallback, finallyCallback) {
                const originalOrder = document.order

                DocumentResource.upload(source, document.type, document.description, (e) => {
                    document.progress = e.loaded / (e.total / 100)
                }).then(response => {
                    document = Object.assign(document, response.data, { order: originalOrder });

                    this.$emit('uploaded', document)

                    successCallback(document)
                }, e => {
                    document.is_deleted = true;

                    if (document.source instanceof File) {

                      let errorMessage = e.message

                      if (e.response && e.response.data && e.response.data.message) {
                        errorMessage = e.response.data.message
                      }

                      alert('There was a problem uploading \'' + document.source.name +'\'. ' + errorMessage)
                    }

                    handleApiError(e);

                    failureCallback();
                }).finally(finallyCallback);
            },
            addDocuments(files) {
                this.canSave = false;
                this.$emit('can-save', this.canSave)

                let invalidFiles = []
                let baseIndex = this.documents.length;
                let queue = new PromiseQueue()
                queue.haltOnError = true

                let finishWork = () => {
                    if (queue.length() === 0) {
                        this.canSave = true;
                        this.$emit('can-save', this.canSave)
                        this.$emit('selected', this.documents)
                    }
                }

                for (let i in files) {
                    if (files[i] instanceof File) {
                        if (this.accept !== '*') {
                            const accepts = this.accept.split(",")

                            const type = files[i].type

                            const valid = accepts.filter(mime => {
                                return type.match(new RegExp(mime))
                            })

                            if (valid.length === 0) {
                                invalidFiles.push(files[i])
                                continue
                            }
                        }

                        let doc = new DocumentSchema({
                            source: files[i],
                            type: this.defaultType,
                            description: this.descriptions[this.descriptions.length - 1],
                            is_deleted: false,
                            is_loaded: false,
                            is_dirty: true,
                            order: parseInt(i) + baseIndex,
                            preview_url: null,
                            progress: 0
                        });

                        this.$emit('added', doc)

                        if (this.selectBeforeUpload) {
                            this.documents.push(doc);

                            this.setDocumentOrders()

                            this.emitDocumentCount()
                        }

                        queue.enqueue(() => new Promise((resolve, reject) => {
                            if (AcceptedImageMimes.split(',').indexOf(doc.source.type) === -1) {
                                this.uploadDocument(doc, doc.source, resolve, reject, finishWork)
                            } else {

                                (new Promise((resolve, reject) => {
                                    if (doc.source.type === 'image/heic') {
                                        import("heic2any"/* webpackChunkName: "heic2any" */).then(({default: heic2any}) => {
                                            heic2any({
                                                blob: doc.source,
                                                toType: "image/png",
                                                quality: 1, // cuts the quality and size by half
                                            }).then(response => {
                                                resolve(response)
                                            })
                                        })
                                    } else {
                                        resolve(doc.source)
                                    }
                                })).then(blob => {
                                    new Compressor(blob, {
                                        quality: window.marque.ui.tweaks.image_compression.quality,
                                        maxWidth: window.marque.ui.tweaks.image_compression.max_width,
                                        maxHeight: window.marque.ui.tweaks.image_compression.max_height,

                                        success: (result) => {
                                            this.uploadDocument(doc, result, resolve, reject, finishWork)
                                        },
                                        error(err) {
                                            throw new Error(
                                                'There was an error compressing an image.',
                                                {cause: err}
                                            );
                                        },
                                    });
                                })
                            }
                        }))
                    }
                }

                if (invalidFiles.length > 0) {
                    alert(`${invalidFiles.length} files were not added as they were the wrong type of file`)
                }

                queue.dequeue()

            },
            dropped(e) {
                this.addDocuments(e.dataTransfer.files);
            },
            dragging(e) { /**/ },
            save() {
                this.loaded = false;
                this.canSave = false

                this.setDocumentOrders()

                this.$emit('can-save', this.canSave)

                let toAssociate = this.documents.filter(item => {
                    return !item.is_deleted && item.is_dirty;
                });

                let toRemove = this.documents.filter(item => {
                    return item.is_deleted && item.uuid;
                });

                if (!this.shouldAssociate) {
                    this.$emit('saved', { associated: false, toAssociate, toRemove })
                    this.loaded = true
                    this.canSave = true
                    this.$emit('can-save', this.canSave)
                    return
                }

                const chain = []

                if (toAssociate.length > 0) {
                    chain.push(DealerKit.documents.associateDocuments({
                        owner_id: this.modelId,
                        owner_type: this.modelType,
                        documents: toAssociate.map(document => {
                            return {
                                type: document.type,
                                description: document.description,
                                order: document.order,
                                id: document.id
                            }
                        })
                    }))
                }

                if (toRemove.length > 0) {
                    chain.push(DealerKit.documents.destroyDocuments({
                        ids: toRemove.map(document => document.id)
                    }))
                }

                Promise.all(chain).then(() => {
                    this.$emit('saved', { associated: true, toAssociate, toRemove })
                }).finally(() => {
                    this.loaded = true
                    this.canSave = true
                    this.$emit('can-save', this.canSave)
                }).catch(handleApiError)
            }
        },
        computed: {
            qrUrl() {
                if (!window.marque.ui.tweaks.document_qr_upload) {
                    return false;
                }

                let hash = btoa(JSON.stringify({
                    owner: {type: this.modelType, id: this.modelId},
                    document_pane_id: this.paneId,
                    type: this.defaultType,
                    description: this.defaultDescription,
                    accepts: this.accept,
                }));

                return `${window.marque.ui.user.intray_upload_url}/qr?h=${hash}`
            },
            defaultDescription() {
                return this.descriptions[this.descriptions.length - 1]
            },
            attachedDocuments: {
                get() {
                    return sortBy(this.documents.filter(document => !document.is_deleted), 'order')
                },
                set(value) {
                    const deleted = this.documents.filter(document => document.is_deleted)

                    this.documents = value.concat(deleted)

                    this.setDocumentOrders()
                }
            },
            slotProps() {
                return {
                    is_dirty: this.documents.filter(item => item.is_dirty).length >0,
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .handle { user-select: none; }
    .image-preview { position: relative; }
    .image-options { display: none; }
    *[class^="col-"]:hover .image-options {
        background: rgba(0,0,0,0.7);
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        color: #fff;
        display: block;
    }

    .thumbnail-actions {
        align-items: center;
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
    }
    .thumbnail-actions > * {
        vertical-align: middle;
    }
    .thumbnail-info-bar {
        background: rgba(0, 0, 0, 0.8);
        bottom: 0;
        box-sizing: border-box;
        color: #fff;
        display: block;
        font-size: .75rem;
        left: 0;
        line-height: 1.3;
        padding: .4rem .5rem;
        position: absolute;
        text-align: center;
        width: 100%;
        pointer-events: none;
        z-index: 2;
    }

    .drop-target {
        border: 5px dashed #f0f0f0;
        padding: 2rem;
        text-align: center;
        margin-bottom: 2rem;
    }
</style>
